import { joinIds, splitIds, updateWithToast, updateWithToastPromise } from '@/clientOnlyUtils/clientUtils';
import { AnimatePresence } from 'framer-motion';
import React from 'react';
import { toast } from 'react-hot-toast';

import { TeamUserAvatar } from '@poseidon/components/Avatar';
import { ESC_PRIORITY, useEscStack } from '@poseidon/components/contexts';
import {
  Button,
  ButtonGroup,
  type CampaignOverrideMode,
  CampaignTemplateFilterSelectWithOverride,
  FilterSelect,
  OwnerFilterSelect,
  StageFilterSelect,
} from '@poseidon/components/Input2';
import { DeleteCadencePopup, MarkDonePopup } from '@poseidon/components/Popup';
import { Arrows, Development, General, Media, Users } from '@poseidon/components/StrokeIcons';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';
import { hasItems } from '@poseidon/shared-utils';

import { useAiStepBulkCreateMutation } from '@/hooks/trpc/useAiStepMutation';
import {
  useBulkChangeCadenceMutation,
  useCadenceChangeUserBulkMutation,
  useCadenceDeleteAllMutation,
  useCadencePauseAllMutation,
  useCadenceResumeAllMutation,
} from '@/hooks/trpc/useCadenceMutation';
import { useSequenceAssignMutation } from '@/hooks/trpc/useSequenceMutation';
import {
  useContactBulkAckInteraction,
  useContactBulkSetStageMutation,
  useEnhanceContactMutation,
  useGenerateDossiersBulkMutation,
  useResearchFromLinkedInMutation,
} from '@/hooks/trpc/useContactMutation';
import { ExportContactsModal } from '~/components/SelectedPopup/ExportContactsModal';
import { useGlobalReminderDeleteBulk } from '@/hooks/trpc/useGlobalReminders';
import { useBulkAddTagToContactsMutation } from '@/hooks/trpc/useTagMutation';
import { useUpdateTeamData } from '@/hooks/trpc/useUserMutation';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import { api } from '@/utils/api';
import { useAuthContext } from '~/auth/auth.context';
import { TagFilterSelectNextJS } from '~/components/TagFilterSelectNextJS';
import SelectPopupContainer from '~/components/SelectedPopup/SelectPopupContainer';
import { SkippedContactsPopup } from '~/components/SkippedContactsPopup';
import { useIsAdminLike } from '~/hooks/useSettingsPermissions';

type ContactSelectedPopupProps = {
  teamId: string;
  userId: string;
  isPremium: boolean;
  creditsRemaining: number;
};

export const ContactSelectedPopup = (props: ContactSelectedPopupProps) => {
  const [query] = useRouterQuery();
  const [skippedContactPublicIds, setSkippedContactPublicIds] = React.useState<string[]>([]);

  const aiStepBulkCreateMutation = useAiStepBulkCreateMutation();

  const onAddSkippedAnyway = (contactPublicIds: string[]) =>
    aiStepBulkCreateMutation.mutateAsync({
      teamId: props.teamId,
      userId: props.userId,
      data: { contactIds: contactPublicIds, type: 'message' },
      onlyNewPeople: false,
    });

  return (
    <>
      <AnimatePresence>
        {hasItems(query.selectedIds) && (
          <ContactSelectedPopupWithIds
            {...props}
            selectedIds={query.selectedIds ?? []}
            onSkipped={setSkippedContactPublicIds}
          />
        )}
      </AnimatePresence>
      {skippedContactPublicIds.length > 0 && (
        <SkippedContactsPopup
          teamId={props.teamId}
          skippedContactPublicIds={skippedContactPublicIds}
          onClose={() => setSkippedContactPublicIds([])}
          onAddAnyway={onAddSkippedAnyway}
        />
      )}
    </>
  );
};

interface ContactSelectedPopupWithIdsProps extends ContactSelectedPopupProps {
  selectedIds: string[];
  onSkipped: (contactPublicIds: string[]) => void;
}
export const ContactSelectedPopupWithIds = ({
  teamId,
  userId,
  isPremium,
  creditsRemaining,
  selectedIds,
  onSkipped,
}: ContactSelectedPopupWithIdsProps) => {
  const [loading, setLoading] = React.useState<string>();
  const [shouldDeleteReminders, setShouldDeleteReminders] = React.useState(false);
  const [shouldResolveEvents, setShouldResolveEvents] = React.useState(false);
  const [shouldDeleteCadences, setShouldDeleteCadences] = React.useState(false);

  const [auth] = useAuthContext();
  const authedUserTeam = 'userTeam' in auth ? auth.userTeam : null;
  const onlyNewPeople = authedUserTeam?.takeActionOnlyNewPeople ?? true;
  const campaignOverrideMode: CampaignOverrideMode = authedUserTeam?.campaignOverrideMode ?? 'smart_override';

  const utils = api.useUtils();
  const updateTeamDataMutation = useUpdateTeamData();
  const setOnlyNewPeople = (next: boolean) => {
    if (next === onlyNewPeople) return;
    // Optimistically flip the chevron — feels instant. The shared
    // useUpdateTeamData hook invalidates user.me on success (which
    // reconciles against the server) and we rollback on error.
    const prev = utils.user.me.getData();
    utils.user.me.setData(undefined, (old) => {
      if (!old || !('userTeam' in old) || !old.userTeam) return old;
      return { ...old, userTeam: { ...old.userTeam, takeActionOnlyNewPeople: next } };
    });
    updateTeamDataMutation.mutate(
      { teamId, userId, data: { takeActionOnlyNewPeople: next } },
      {
        onError: () => {
          if (prev) utils.user.me.setData(undefined, prev);
        },
      },
    );
  };
  const setCampaignOverrideMode = (next: CampaignOverrideMode) => {
    if (next === campaignOverrideMode) return;
    const prev = utils.user.me.getData();
    utils.user.me.setData(undefined, (old) => {
      if (!old || !('userTeam' in old) || !old.userTeam) return old;
      return { ...old, userTeam: { ...old.userTeam, campaignOverrideMode: next } };
    });
    updateTeamDataMutation.mutate(
      { teamId, userId, data: { campaignOverrideMode: next } },
      {
        onError: () => {
          if (prev) utils.user.me.setData(undefined, prev);
        },
      },
    );
  };

  const isAdminLike = useIsAdminLike();
  const teamUserOptionsQuery = api.team.teamUserOptions.useQuery({ teamId }, { enabled: isAdminLike });
  const userOptions = teamUserOptionsQuery.data ?? [];

  const cadenceTemplatesQuery = api.cadenceTemplate.namesWithOwner.useQuery({ teamId, pagination: { limit: 5000 } });
  const templates =
    cadenceTemplatesQuery.data?.data.map((d) => ({
      id: d.id,
      name: d.name,
      isMine: d.userId === userId,
      errorCount: d.errorSteps?.length ?? 0,
      ownerName: d.ownerName,
      ownerProfilePicture: d.ownerProfilePicture,
    })) ?? [];

  const stagesQuery = api.stage.all.useQuery({ teamId });
  const stages = stagesQuery.data?.stages ?? [];

  // Assigning people to a sequence is done from here (search selection), gated on the flag.
  const sequencesEnabled = authedUserTeam?.sequenceEnabled === true;
  const sequencesQuery = api.sequence.list.useQuery({ teamId }, { enabled: sequencesEnabled });
  const sequences = sequencesQuery.data?.sequences ?? [];

  const [query, setQuery] = useRouterQuery();
  const enhanceMutation = useEnhanceContactMutation();
  const generateDossiersMutation = useGenerateDossiersBulkMutation();
  const [showExportModal, setShowExportModal] = React.useState(false);
  const researchMutation = useResearchFromLinkedInMutation();
  const bulkChangeCadenceMutation = useBulkChangeCadenceMutation();
  const cadenceDeleteAllMutation = useCadenceDeleteAllMutation();
  const cadenceChangeUserMutation = useCadenceChangeUserBulkMutation();
  const cadencePauseAllMutation = useCadencePauseAllMutation();
  const cadenceResumeAllMutation = useCadenceResumeAllMutation();
  const contactBulkAckInteractionMutation = useContactBulkAckInteraction();
  const deleteRemindersMutation = useGlobalReminderDeleteBulk();
  const aiStepBulkCreateMutation = useAiStepBulkCreateMutation();
  const bulkSetStageMutation = useContactBulkSetStageMutation();
  const bulkAddTagMutation = useBulkAddTagToContactsMutation();
  const sequenceAssignMutation = useSequenceAssignMutation();

  const splitSelectedIds = selectedIds.map((c) => splitIds(c));

  const onClose = () => setQuery((q) => ({ ...q, selectedIds: undefined }));
  useEscStack('ContactSelectedPopup', ESC_PRIORITY.ACTION_BAR, onClose);

  const onDelete = async () => {
    const idObj = splitIds(query?.id ?? '');
    const didSelectId = splitSelectedIds.find((s) => s.cadenceId == idObj.cadenceId) != null;

    setShouldDeleteCadences(false);
    await updateWithToast({
      loading: 'Deleting...',
      success: 'Cadences deleted',
      setQuery,
      setLoading,
      onSuccessQuery:
        idObj && didSelectId
          ? { selectedIds: undefined, id: joinIds({ contactId: idObj.contactId }) }
          : { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        cadenceDeleteAllMutation.mutateAsync({
          teamId: teamId,
          cadenceIds: splitSelectedIds.map((c) => c.cadenceId).filter((a) => a != null),
        }),
    });
  };

  const onChangeOwner = (userId: string) =>
    updateWithToast({
      loading: 'Changing Owner...',
      success: userId === '' ? 'Owner removed' : 'Owner changed',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        cadenceChangeUserMutation.mutateAsync({
          data: {
            cadenceIds: splitSelectedIds.map((c) => c.cadenceId).filter((a) => a != null),
            userId: userId || null,
          },
        }),
    });

  const onEnhance = () => {
    const contactIds = selectedIds.map((c) => splitIds(c).contactId).filter((a) => a != null);
    const willEnrich = isPremium && creditsRemaining > 0;

    return updateWithToast({
      loading: willEnrich ? 'Refreshing & enriching...' : 'Refreshing from LinkedIn...',
      success: willEnrich ? 'Contacts refreshed — enrichment running in background' : 'Contacts refreshed from LinkedIn',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: async () => {
        const researchResults = await Promise.all(
          contactIds.map((contactId) =>
            researchMutation
              .mutateAsync({ contactId, teamId: teamId, userId: userId })
              .then((r) => {
                return { ok: true as const, contactId, r };
              })
              .catch((e: unknown) => {
                return { ok: false as const, contactId, error: e };
              }),
          ),
        );

        const experienceThrottledCount = researchResults.filter(
          (res) => res.ok && res.r?.throttledSections?.includes('experience'),
        ).length;
        // Empty-without-throttle is a different condition (truly private / no job history).
        const emptyExperienceCount = researchResults.filter(
          (res) =>
            res.ok &&
            res.r?.experienceFetchedCount === 0 &&
            !res.r?.throttledSections?.includes('experience') &&
            !res.r?.skipped,
        ).length;
        const skippedFreshCount = researchResults.filter((res) => res.ok && res.r?.skipped).length;

        if (experienceThrottledCount > 0) {
          toast(
            experienceThrottledCount === 1
              ? "LinkedIn is currently rate-limiting this profile's experience section. We'll automatically re-fetch in a few minutes — your existing experiences are preserved."
              : `LinkedIn is currently rate-limiting the experience section for ${experienceThrottledCount} contacts. We'll automatically re-fetch in a few minutes — existing experiences are preserved.`,
            { duration: 8000 },
          );
        } else if (emptyExperienceCount > 0) {
          toast(
            emptyExperienceCount === 1
              ? 'LinkedIn returned no work history for 1 contact (private profile or no public job history). Existing experiences were kept.'
              : `LinkedIn returned no work history for ${emptyExperienceCount} contacts (private profiles or no public job history). Existing experiences were kept.`,
            { duration: 6000 },
          );
        } else if (skippedFreshCount > 0) {
          toast(
            skippedFreshCount === 1
              ? 'Contact was refreshed in the last 24h — skipped re-fetching.'
              : `${skippedFreshCount} contacts were refreshed in the last 24h — skipped re-fetching.`,
            { duration: 5000 },
          );
        }
        if (willEnrich) {
          const enhanceResults = await Promise.all(
            contactIds.map((contactId) =>
              enhanceMutation
                .mutateAsync({ contactId })
                .then((r) => {
                  return { ok: true as const, contactId, r };
                })
                .catch((e: unknown) => {
                  return { ok: false as const, contactId, error: e };
                }),
            ),
          );

          // Enhance re-verifies each contact on LinkedIn; when a profile is confirmed
          // dead and unrepairable, the server removes that contact in the same call and
          // reports `deleted`. Nothing to delete client-side — just report how many were
          // removed. (List invalidation is handled by the enhance mutation's onSuccess.)
          const deletedCount = enhanceResults.filter((res) => res.ok && res.r.deleted).length;
          if (deletedCount > 0) {
            toast(
              deletedCount === 1
                ? 'Removed 1 contact whose LinkedIn profile no longer exists on LinkedIn.'
                : `Removed ${deletedCount} contacts whose LinkedIn profiles no longer exist on LinkedIn.`,
              { duration: 6000 },
            );
          }
        }
      },
    });
  };

  const onBulkStartCadence = async (cadenceTemplateId: string) => {
    const isRemoving = cadenceTemplateId === '';
    const contactIds = splitSelectedIds.map((c) => c.contactId).filter((a) => a != null);

    if (isRemoving) {
      return updateWithToast({
        loading: 'Removing from campaign...',
        success: 'Removed from campaign',
        setQuery,
        setLoading,
        onSuccessQuery: { selectedIds: undefined },
        onErrorQuery: { selectedIds: undefined },
        mutationFnAsync: () =>
          bulkChangeCadenceMutation.mutateAsync({
            teamId,
            cadenceTemplateId: null,
            contactIds,
          }),
      });
    }

    await updateWithToastPromise({
      loading: 'Changing cadence...',
      success: 'Cadences changed',
      mutationFnAsync: () =>
        bulkChangeCadenceMutation.mutateAsync({
          teamId,
          cadenceTemplateId,
          contactIds,
        }),
    });

    setQuery((q) => ({ ...q, selectedIds: undefined }));
  };

  const onBulkAddToSequence = async (sequenceId: string) => {
    if (sequenceId === '') return;
    const contactIds = splitSelectedIds.map((c) => c.contactId).filter((a): a is string => a != null);
    await updateWithToast({
      loading: 'Adding to sequence…',
      success: 'Added to sequence',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () => sequenceAssignMutation.mutateAsync({ teamId, sequenceId, contactIds }),
    });
  };

  // When the selection flow was opened from a campaign switch
  // (/prospects?switchId=…), the primary action is "add to switch" — the switch
  // routes each contact into a campaign by attribute, so there's no campaign to
  // pick here. See AddPeopleToSwitchActions.
  const switchId = query.switchId;
  const switchQuery = api.campaignSwitch.get.useQuery(
    { teamId, switchId: switchId ?? '' },
    { enabled: Boolean(switchId) },
  );
  const intakeIntoSwitchMutation = api.campaignSwitch.intakeIntoSwitch.useMutation({
    onSuccess: () => {
      if (switchId == null) return;
      void utils.campaignSwitch.stagingList.invalidate({ teamId, switchId });
      void utils.campaignSwitch.candidateCounts.invalidate({ teamId, switchId });
    },
  });

  const onAddToSwitch = async () => {
    if (switchId == null) return;
    const contactIds = splitSelectedIds.map((c) => c.contactId).filter((a): a is string => a != null);
    if (contactIds.length === 0) return;
    await updateWithToast({
      loading: 'Adding to switch...',
      success: 'Added — routing runs after research',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () => intakeIntoSwitchMutation.mutateAsync({ teamId, switchId, contactIds }),
    });
  };

  const onBulkSetStage = (stageId: string) =>
    updateWithToast({
      loading: 'Changing stage...',
      success: 'Stage updated',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        bulkSetStageMutation.mutateAsync({
          contactIds: splitSelectedIds.map((c) => c.contactId).filter((a) => a != null),
          stageId: stageId || null,
        }),
    });

  const onBulkAddTag = (tagId: string) =>
    updateWithToast({
      loading: 'Adding tag...',
      success: 'Tag added',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        bulkAddTagMutation.mutateAsync({
          contactIds: splitSelectedIds.map((c) => c.contactId).filter((a) => a != null),
          tagId,
        }),
    });

  const onResume = async () => {
    await updateWithToast({
      loading: 'Resuming...',
      success: 'Cadences resumed',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        cadenceResumeAllMutation.mutateAsync({
          cadenceIds: splitSelectedIds.map((c) => c.cadenceId).filter((a) => a != null),
        }),
    });
  };

  const onPause = () =>
    updateWithToast({
      loading: 'Pausing...',
      success: 'Cadences paused',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        cadencePauseAllMutation.mutateAsync({
          cadenceIds: splitSelectedIds.map((c) => c.cadenceId).filter((a) => a != null),
        }),
    });

  const onAddToAiMessage = async () => {
    const contactIds = splitSelectedIds.map((c) => c.contactId).filter((a) => a != null);
    setQuery((q) => ({ ...q, selectedIds: undefined }));
    const toastId = toast.loading('Adding to Take Action...');
    try {
      const result = await aiStepBulkCreateMutation.mutateAsync({
        teamId: teamId,
        userId: userId,
        data: { contactIds, type: 'message' },
        onlyNewPeople,
      });
      // When previously-added contacts were skipped, the SkippedContactsPopup
      // tells the full story — don't double up with a toast.
      if (result.skippedContactIds.length > 0) {
        toast.dismiss(toastId);
        onSkipped(result.skippedContactIds);
      } else {
        const skippedReasons: string[] = [];
        if (result.skippedDisqualified > 0) {
          skippedReasons.push(`${result.skippedDisqualified} disqualified`);
        }
        if (result.skippedAlreadyInQueue > 0) {
          skippedReasons.push(`${result.skippedAlreadyInQueue} already in queue`);
        }
        const skippedMessage = skippedReasons.length > 0 ? `. Skipped: ${skippedReasons.join(', ')}` : '';
        toast.success(`Added ${result.created} to Take Action${skippedMessage}`, { id: toastId });
      }
    } catch {
      toast.error('Failed to add to Take Action', { id: toastId });
    }
  };

  const onAcknowledge = async () => {
    setShouldResolveEvents(false);
    await updateWithToast({
      loading: 'Acknowledging...',
      success: 'Acknowledged views',
      setQuery,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        contactBulkAckInteractionMutation.mutateAsync({
          viewIds: splitSelectedIds.map((c) => c.interactionId).filter((a) => a != null),
        }),
    });
  };

  const onDeleteReminders = async () => {
    setShouldDeleteReminders(false);
    await updateWithToast({
      loading: 'Deleting...',
      success: 'Reminders deleted',
      setQuery,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        deleteRemindersMutation.mutateAsync({
          globalReminderIds: splitSelectedIds.map((c) => c.reminderId).filter((a) => a != null),
        }),
    });
  };

  const cadenceIdsLength = splitSelectedIds.filter((a) => a.cadenceId != null).length ?? 0;
  const contactIdsLength = splitSelectedIds.filter((a) => a.contactId != null).length ?? 0;
  const selectedContactIds = splitSelectedIds.map((a) => a.contactId).filter((a): a is string => a != null);

  // Dossier synthesis runs on the clicking user's Anthropic key — hide the
  // button when it isn't configured (flag-gating rule: never surface an
  // affordance that can only error). Tavily research falls back to the
  // org-wide master key server-side, so a personal Tavily key is optional;
  // the route still fails loudly if neither exists.
  const canGenerateDossiers = authedUserTeam?.anthropicApiKey != null && authedUserTeam.anthropicApiKey.length > 0;

  const onGenerateDossiers = () =>
    updateWithToast({
      loading: 'Queueing dossier research…',
      success: 'Dossier generation queued',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      mutationFnAsync: async () => {
        const result = await generateDossiersMutation.mutateAsync({
          teamId,
          contactIds: selectedContactIds,
        });
        if (result.queued === 0 && result.skippedFresh > 0) {
          toast(`All ${result.skippedFresh} dossiers are fresh (under ~6 months) — nothing to regenerate.`);
        } else if (result.skippedFresh > 0) {
          toast(`Queued ${result.queued} — skipped ${result.skippedFresh} with a fresh dossier.`);
        }
      },
    });
  const reminderIdsLength = splitSelectedIds.filter((a) => a.reminderId != null).length ?? 0;
  const interactionIdsLength = splitSelectedIds.filter((a) => a.interactionId != null).length ?? 0;

  return (
    <SelectPopupContainer loading={loading}>
      {reminderIdsLength > 0 && (
        <Button size="sm" icon={<General.Check />} onClick={() => setShouldDeleteReminders(true)}>
          Mark Reminder as Done
        </Button>
      )}

      {interactionIdsLength > 0 && (
        <Button size="sm" icon={<General.Eye />} onClick={() => setShouldResolveEvents(true)}>
          Acknowledge
        </Button>
      )}

      {contactIdsLength > 0 && canGenerateDossiers && (
        <Button
          size="sm"
          icon={generateDossiersMutation.isPending ? <WaveSpinner /> : <General.SearchSm />}
          disabled={generateDossiersMutation.isPending}
          onClick={onGenerateDossiers}
          tooltip={`Research + generate a dossier for ${contactIdsLength === 1 ? 'this contact' : `each of the ${contactIdsLength} contacts`} (skips dossiers newer than ~6 months)`}>
          Generate dossiers
        </Button>
      )}

      {contactIdsLength > 0 && (
        <Button
          size="sm"
          icon={<General.Download01 />}
          onClick={() => setShowExportModal(true)}
          tooltip={`Export ${contactIdsLength === 1 ? 'this contact' : `${contactIdsLength} contacts`} as CSV — pick columns incl. dossier sections`}>
          Export CSV
        </Button>
      )}

      {contactIdsLength > 0 && (
        <Button
          size="sm"
          icon={enhanceMutation.isPending || researchMutation.isPending ? <WaveSpinner /> : <Arrows.RefreshCw04 />}
          disabled={enhanceMutation.isPending || researchMutation.isPending}
          onClick={onEnhance}
          tooltip={
            isPremium && creditsRemaining > 0
              ? `Refresh & enrich ${contactIdsLength} ${contactIdsLength === 1 ? 'contact' : 'contacts'} (uses up to ${Math.min(contactIdsLength, creditsRemaining).toLocaleString()} of ${creditsRemaining.toLocaleString()} credits)`
              : `Refresh LinkedIn data only — no enrichment credits remaining`
          }>
          Enhance
        </Button>
      )}

      {cadenceIdsLength > 0 && (
        <ButtonGroup size="sm">
          <Button
            icon={<Media.PlayCircle />}
            onClick={onResume}
            tooltip={`Resume${selectedIds.length > 1 ? ` ${selectedIds.length}` : ''}`}
          />
          <Button
            icon={<Media.PauseCircle />}
            onClick={onPause}
            tooltip={`Pause${selectedIds.length > 1 ? ` ${selectedIds.length}` : ''}`}
          />
        </ButtonGroup>
      )}

      {contactIdsLength > 0 && (
        <ButtonGroup size="sm">
          <FilterSelect
            value={onlyNewPeople ? 'only_new' : 'everyone'}
            options={[
              {
                value: 'only_new',
                label: 'Only new people',
                description: 'Skip contacts you have ever added to Take Action',
                icon: <Users.UserPlus01 />,
              },
              {
                value: 'everyone',
                label: 'Everyone',
                description: 'Add all selected contacts, even ones you previously added',
                icon: <Users.Users01 />,
              },
            ]}
            onChange={(v) => setOnlyNewPeople(v === 'only_new')}
            tooltip={
              onlyNewPeople
                ? 'Only contacts you have never added to Take Action will be added'
                : 'All selected contacts will be added, even ones you previously added to Take Action'
            }
            filterable={false}
            showText={false}
            autoWidth
          />
          <Button onClick={onAddToAiMessage} icon={<General.Zap />} tooltip="Add to Take Action">
            {/* Collapses to just the lightning bolt in a narrow container. */}
            <span className="hidden @[900px]:inline">Add to Take Action</span>
          </Button>
        </ButtonGroup>
      )}

      {cadenceIdsLength > 0 && isAdminLike && (
        <OwnerFilterSelect
          userOptions={userOptions}
          value={undefined}
          onChange={onChangeOwner}
          placeholder="Owner"
          size="sm"
          tooltip="Change owner"
        />
      )}

      {contactIdsLength > 0 && switchId == null && (
        <CampaignTemplateFilterSelectWithOverride
          templates={templates}
          includeRemoveOption
          renderOwnerIcon={({ ownerProfilePicture, ownerName }) => (
            <TeamUserAvatar
              userTeam={{ profilePicture: ownerProfilePicture, userTeamName: ownerName }}
              className="size-full"
              shape="circle"
            />
          )}
          onChange={onBulkStartCadence}
          overrideMode={campaignOverrideMode}
          onOverrideModeChange={setCampaignOverrideMode}
          placeholder="Campaign"
          size="sm"
        />
      )}

      {contactIdsLength > 0 && switchId == null && sequencesEnabled && sequences.length > 0 && (
        <FilterSelect
          icon={<Development.Dataflow04 />}
          placeholder="Sequence"
          value=""
          options={sequences.map((s) => ({ value: s.id, label: s.name }))}
          onChange={onBulkAddToSequence}
          size="sm"
          popoverPlacement="top-start"
        />
      )}

      {contactIdsLength > 0 && switchId != null && (
        <Button size="sm" color="blue" icon={<General.Plus />} onClick={onAddToSwitch}>
          Add to switch{switchQuery.data?.name ? ` "${switchQuery.data.name}"` : ''}
        </Button>
      )}

      {contactIdsLength > 0 && (
        <ButtonGroup size="sm">
          <StageFilterSelect
            stages={stages}
            includeRemoveOption
            onChange={onBulkSetStage}
            placeholder="Stage"
            showText={false}
          />
          <TagFilterSelectNextJS
            teamId={teamId}
            onChange={onBulkAddTag}
            allowCreate
            contactOnly
            placeholder="Tag"
            showText={false}
          />
        </ButtonGroup>
      )}

      {shouldDeleteCadences && (
        <DeleteCadencePopup onClose={() => setShouldDeleteCadences(false)} onDelete={() => onDelete()} />
      )}
      {shouldResolveEvents && (
        <MarkDonePopup onClose={() => setShouldResolveEvents(false)} onSubmit={() => onAcknowledge()} />
      )}
      {shouldDeleteReminders && (
        <MarkDonePopup onClose={() => setShouldDeleteReminders(false)} onSubmit={() => onDeleteReminders()} />
      )}
      {showExportModal && (
        <ExportContactsModal
          teamId={teamId}
          target={{ kind: 'selection', contactIds: selectedContactIds }}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </SelectPopupContainer>
  );
};
